/**
 * One mapping rule on one line: the output field, the sources it is read from
 * in order, and a `.*` mark when a regex transforms the value (the regex
 * itself in the mark's tooltip). Shared by the list, the editor, the combined
 * row preview and the read-only combined row.
 */

import React from 'react';
import { useTranslate } from 'react-admin';
import { Tooltip, Typography, useTheme } from '@mui/material';

export interface RuleSummaryProps {
  output: string;
  sources: string[];
  regex?: string;
}

export const RuleSummary: React.FC<RuleSummaryProps> = ({ output, sources, regex }) => {
  const t = useTranslate();
  const theme = useTheme();
  if (!output && sources.length === 0) return null;
  const regexLabel = t('screens.fieldMappings.regexMark', { regex });

  return (
    <Typography variant="body2" component="span" sx={{ fontFamily: theme.custom.text.mono, overflowWrap: 'anywhere' }}>
      {output}
      {sources.length > 0 && (
        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
          {' '}← {sources.join(', ')}
        </Typography>
      )}
      {regex && (
        <Tooltip title={regexLabel} enterTouchDelay={0}>
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            tabIndex={0}
            aria-label={regexLabel}
            sx={{ fontFamily: 'inherit', ml: 0.75, cursor: 'help' }}
          >
            .*
          </Typography>
        </Tooltip>
      )}
    </Typography>
  );
};

/** The display name of a rule's source: the KYC field, or a fixed value in quotes. */
export const sourceName = (source: { kycField?: { name?: string } | null; prefix?: string }): string =>
  source.kycField?.name || (source.prefix ? `"${source.prefix}"` : '');

export default RuleSummary;
